/**
 * Zod schema for the UW Information form. Single source of truth for
 * both runtime validation (via @hookform/resolvers/zod) and the
 * TypeScript shape the form consumes.
 *
 * Field names mirror the v2 backend (orion-backend PR #253). Where the
 * UI keeps a richer representation than the wire payload (`gl`/`pl`
 * booleans, duration unit toggle), the adapters in
 * `src/api/adapters/submission.ts` bridge the two.
 */
import { z } from "zod";
import { INVALID_DATE_MESSAGE, isEmptyOrValidUsDate, isValidUsDate } from "../../../utils/date-validation";

/** Shorter "required" copy used by the coverage-date triplet
 *  (Renewal / Effective / Expiration) per design spec. */
const REQUIRED_SHORT = "This is a required field.";

/** Accepts blank or a real MM/DD/YYYY calendar date. The adapter
 *  converts to ISO on save. */
const dateString = z.string().refine(isEmptyOrValidUsDate, INVALID_DATE_MESSAGE);

/** Required MM/DD/YYYY date with the shorter "required" copy — the
 *  coverage-date triplet (Renewal / Effective / Expiration). */
const requiredCoverageDate = z.string().min(1, REQUIRED_SHORT).refine(isValidUsDate, INVALID_DATE_MESSAGE);

// ────────────────────────── Sub-schemas ───────────────────────

export const accountBasicsSchema = z.object({
    // Required: the backend rejects an empty title with a 422
    // (string_too_short). Flagging it here surfaces the inline error
    // instead of silently blocking Save & Continue (OR-1274). Uses the
    // short copy ("This is a required field.") to match Renewal/Expiration.
    title: z.string().min(1, REQUIRED_SHORT),
    fni: z.string(),
    mailingAddress: z.string(),
    businessDescription: z.string(),
    type: z.enum(["New Business", "Renewal", "Endorsement", "Tail", ""]),
    renewalDate: requiredCoverageDate,
    expirationDate: requiredCoverageDate,
    submissionDate: dateString,
    /** Form keeps premium/deductible as raw strings so empty stays
     *  empty; adapter parses to number on save. */
    expiringPremium: z.string().refine((v) => v === "" || /^\d+(\.\d+)?$/.test(v.replace(/,/g, "")), "Numbers only"),
    expiringDeductible: z.string().refine((v) => v === "" || /^\d+(\.\d+)?$/.test(v.replace(/,/g, "")), "Numbers only"),
    accountManagerName: z.string(),
    accountManagerEmail: z.string().email("Invalid email").or(z.literal("")),
});

export const brokerageSchema = z.object({
    // The brokerage selector is the only editable control here — and
    // it's optional (`broker_id: str | None` on the v2 backend, saving
    // without picking a brokerage is fine).
    //
    // The other fields are read-only in the UI: they always reflect
    // the selected brokerage row (or empty when nothing is selected).
    // No validation runs against them — they're derived state, not user
    // input.
    brokerId: z.string(),
    brokerEmail: z.string(),
    brokerName: z.string(),
    brokerageName: z.string(),
    brokerageAddress: z.string(),
});

export const geminiContactsSchema = z.object({
    riskManagerName: z.string(),
    riskManagerEmail: z.string().email("Invalid email").or(z.literal("")),
    riskManagerPhone: z.string(),
    administratorName: z.string(),
    administratorEmail: z.string().email("Invalid email").or(z.literal("")),
    administratorPhone: z.string(),
    administratorDuration: z
        .string()
        .refine((v) => v === "" || (Number.isFinite(Number(v)) && Number(v) >= 0), "Must be a positive number"),
    administratorDurationUnit: z.enum(["years", "months"]),
});

/** Both insured-row schemas have every field optional — nothing here
 *  blocks Save & Continue. Per design feedback (jun-30) the only required
 *  fields in the UW Information view are the asterisked Account Basics ones
 *  (Submission Title / Renewal Date / Expiration Date); Retro Date and
 *  Entity Name must NOT gate the flow. `retroactiveDate` keeps a format
 *  check only (blank is fine; a typed-but-invalid date is still flagged).
 *  The backend accepts a null entity_name, so a row with just a retro date
 *  persists fine. */
export const namedInsuredSchema = z
    .object({
        id: z.string(),
        entityName: z.string(),
        // Optional on the v2 backend (free string, nullable). UI keeps
        // the enum just for the dropdown options.
        // Accepts presets ("parent" / "subsidiary" / "affiliate" / "dba")
        // OR any custom label the user typed via the dropdown's
        // "Custom Value" affordance.
        relationship: z.string(),
        retroactiveDate: dateString,
    });

export const additionalInsuredSchema = z
    .object({
        id: z.string(),
        entityName: z.string(),
        gl: z.boolean(),
        pl: z.boolean(),
        retroactiveDate: dateString,
    });

// ────────────────────────── Root schema ───────────────────────
//
// NOTE: `locations` is intentionally NOT part of this schema. The
// Schedule of Location table was removed from UW Information (Linda,
// 2026-05-19) and facilities are now owned + edited + persisted by the
// Schedule section (`useFacilities`). Validating locations here blocked
// the UW Info Save on data this view can neither show, fix, nor save.

export const uwInformationSchema = z.object({
    accountBasics: accountBasicsSchema,
    brokerage: brokerageSchema,
    geminiContacts: geminiContactsSchema,
    note: z.string().max(600, "600 characters max"),
    namedInsureds: z.array(namedInsuredSchema),
    additionalInsureds: z.array(additionalInsuredSchema),
});

export type UwInformationFormData = z.infer<typeof uwInformationSchema>;

// ────────────────────────── Row factories ─────────────────────

let rowIdCounter = 0;
function clientRowId(prefix: string): string {
    rowIdCounter += 1;
    return `${prefix}-new-${Date.now()}-${rowIdCounter}`;
}

/** New rows start with empty `id`. The save flow uses that as the
 *  signal to POST instead of PATCH; once the server returns, the
 *  child id replaces it. The `clientRowId()` is only here so React
 *  Hook Form's `useFieldArray` has a stable key while editing. */
export const emptyNamedInsured = (): z.infer<typeof namedInsuredSchema> => ({
    id: clientRowId("ni"),
    entityName: "",
    relationship: "",
    retroactiveDate: "",
});

export const emptyAdditionalInsured = (): z.infer<typeof additionalInsuredSchema> => ({
    id: clientRowId("ai"),
    entityName: "",
    gl: false,
    pl: false,
    retroactiveDate: "",
});

